import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { COMPANY } from "@/lib/site";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Merci de renseigner votre nom, email et message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message envoyé ! Nous vous répondrons rapidement.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec de l'envoi.");
    } finally {
      setSending(false);
    }
  };

  const info = [
    { icon: MapPin, label: "Siège social", value: COMPANY.address },
    { icon: Clock, label: "Disponibilité", value: COMPANY.hours },
    { icon: Mail, label: "Email", value: "contact@goldencab-transport.fr" },
    { icon: Phone, label: "Réservation", value: "En ligne 24/7" },
  ];

  return (
    <div data-testid="contact-page" className="pt-32 pb-24">
      <section className="mx-auto max-w-7xl px-5 lg:px-10">
        <Reveal>
          <p className="text-gold text-xs tracking-[0.4em] uppercase">Nous contacter</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-white">Contact</h1>
          <p className="mt-5 max-w-2xl text-white/60">
            Une demande particulière, un devis longue distance ou un événement ? Notre conciergerie vous répond.
          </p>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-2 gap-12">
          <Reveal>
            <form onSubmit={submit} data-testid="contact-form" className="rounded-2xl border border-white/10 bg-panel p-8 lg:p-10 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-white/70">Nom complet *</Label>
                  <Input data-testid="contact-name" value={form.name} onChange={set("name")} className="mt-2 bg-ink border-white/15 focus-visible:ring-gold" placeholder="Jean Dupont" />
                </div>
                <div>
                  <Label className="text-white/70">Téléphone</Label>
                  <Input data-testid="contact-phone" value={form.phone} onChange={set("phone")} className="mt-2 bg-ink border-white/15 focus-visible:ring-gold" placeholder="06 12 34 56 78" />
                </div>
              </div>
              <div>
                <Label className="text-white/70">Email *</Label>
                <Input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} className="mt-2 bg-ink border-white/15 focus-visible:ring-gold" placeholder="jean@email.com" />
              </div>
              <div>
                <Label className="text-white/70">Message *</Label>
                <Textarea data-testid="contact-message" value={form.message} onChange={set("message")} rows={5} className="mt-2 bg-ink border-white/15 focus-visible:ring-gold" placeholder="Votre demande…" />
              </div>
              <button
                type="submit" disabled={sending} data-testid="contact-submit"
                className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-medium text-black transition-colors duration-300 hover:bg-goldsoft disabled:opacity-60"
              >
                {sending ? "Envoi…" : "Envoyer le message"} <Send size={16} />
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="space-y-4">
              {info.map((it) => (
                <div key={it.label} className="flex items-start gap-4 rounded-xl border border-white/10 bg-panel p-6">
                  <span className="grid h-11 w-11 place-items-center rounded-full bg-gold/15 text-gold shrink-0">
                    <it.icon size={18} />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-white/40">{it.label}</p>
                    <p className="mt-1 text-white/80">{it.value}</p>
                  </div>
                </div>
              ))}
              <div className="rounded-xl border border-gold/25 bg-gold/5 p-6">
                <p className="text-white/80 text-sm leading-relaxed">
                  Pour une réservation immédiate avec paiement sécurisé, utilisez notre tunnel de réservation en ligne.
                </p>
                <a href="/reservation" className="mt-3 inline-block text-gold text-sm hover:underline">Réserver en ligne →</a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
